import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PageComponent} from "../../../../core/shared/mvs-page/page.component";
import {MvsCoreService} from "../../../../core/shared/service/mvs-core.service";
import {HelpArticleService} from "../../service/api/help-article.service";
import {HelpCategoryService} from "../../service/api/help-category.service";
import {HelpArticleDto} from "../../model/dto/help-article.dto";
import {HelpCategoryDto} from "../../model/dto/help-category.dto";

/**
 * Admin page for managing help articles, categories, glossary, and tours.
 * Accessible with HELP_AUTHOR or HELP_ADMIN permission.
 */
@Component({
    selector: 'mvs-help-admin-page',
    templateUrl: './help-admin.page.html',
    styleUrls: ['./help-admin.page.scss'],
    standalone: false
})
export class LpHelpAdminPage extends PageComponent implements OnInit, OnChanges, OnDestroy {

    defaultLabel: string = "Hilfe Administration";
    moduleAlias: string;
    activeTab = 'articles';
    activeTabIndex = 0;
    articles: HelpArticleDto[] = [];
    categories: HelpCategoryDto[] = [];
    isLoading = true;

    // Editor state
    showEditor = false;
    editingArticleId: number = null;

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService,
        private router: Router,
        private articleService: HelpArticleService,
        private categoryService: HelpCategoryService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.moduleAlias = this.route.snapshot.paramMap.get('moduleAlias') || 'bm';
        this.loadData();
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
    }

    private loadData(): void {
        this.isLoading = true;
        this.articleService.listArticles(this.moduleAlias).subscribe({
            next: articles => {
                this.articles = articles;
                this.isLoading = false;
            },
            error: err => {
                console.error('Failed to load articles', err);
                this.isLoading = false;
            }
        });

        this.categoryService.getCategoryTree(this.moduleAlias).subscribe({
            next: categories => this.categories = categories,
            error: err => console.error('Failed to load categories', err)
        });
    }

    onNewArticle(): void {
        this.editingArticleId = null;
        this.showEditor = true;
    }

    onEditArticle(article: HelpArticleDto): void {
        this.editingArticleId = article.id;
        this.showEditor = true;
    }

    onArticleSaved(article: HelpArticleDto): void {
        this.showEditor = false;
        this.loadData();
    }

    onEditorCancelled(): void {
        this.showEditor = false;
    }

    onPublishArticle(article: HelpArticleDto): void {
        this.articleService.publishArticle(article.id).subscribe({
            next: () => this.loadData(),
            error: err => console.error('Failed to publish article', err)
        });
    }

    onChangeStatus(article: HelpArticleDto, status: string): void {
        this.articleService.changeStatus(article.id, status).subscribe({
            next: () => this.loadData(),
            error: err => console.error('Failed to change article status', err)
        });
    }

    getStatusLabel(status: any): string {
        const labels = {0: 'Entwurf', 1: 'In Pruefung', 2: 'Veroeffentlicht', 3: 'Archiviert'};
        return labels[status] || status;
    }

    getStatusSeverity(status: any): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        const severities: Record<number, 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast'> = {
            0: 'info',
            1: 'warn',
            2: 'success',
            3: 'danger'
        };
        return severities[status] || 'info';
    }
}
